import type {
  Facility,
  Operator,
  Zone,
  StaticSensor,
  SensorState,
  Sensor,
  NearbyZonesResponse,
  MapSensor,
  GeoJSONPolygon,
} from '@/types'

// Disable console methods in production for security
if (import.meta.env.PROD) {
  console.log = () => {}
  console.warn = () => {}
  console.error = () => {}
  console.debug = () => {}
  console.info = () => {}
}

// Always use production worker - local dev doesn't have secrets
export const API_BASE = 'https://map-proxy.parkezesolutions.net/api'

interface FetchOptions {
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  body?: string
}

/**
 * Parse parking_space_boundary - it may come as a JSON string from the API
 */
function parseParkingSpaceBoundary(boundary: unknown): GeoJSONPolygon | undefined {
  if (!boundary) return undefined
  
  // If it's already an object with the right shape, return it
  if (typeof boundary === 'object' && boundary !== null && 'type' in boundary && 'coordinates' in boundary) {
    return boundary as GeoJSONPolygon
  }
  
  // If it's a string, try to parse it
  if (typeof boundary === 'string') {
    try {
      const parsed = JSON.parse(boundary)
      if (parsed && parsed.type === 'Polygon' && Array.isArray(parsed.coordinates)) {
        return parsed as GeoJSONPolygon
      }
    } catch (e) {
      console.warn('Failed to parse parking_space_boundary:', e)
    }
  }
  
  return undefined
}

/**
 * API Client for Parkeze Public Map
 * 
 * All requests go through the Cloudflare Worker proxy which handles:
 * - OAuth token management
 * - Rate limiting
 * 
 * Pattern from metadata-api:
 * 1. Call /zones with user's lat/lon to discover zones + operator_ids
 * 2. For each operator_id, fetch /sensors?operator_id=X (combined data)
 * 3. Subscribe to livestream with operator_ids for real-time updates
 */
class ApiClient {
  /**
   * Make an authenticated request to the API
   */
  private async request<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: options.body,
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || `API error: ${response.status}`)
    }
    
    return response.json()
  }
  
  /**
   * Health check
   */
  async health(): Promise<{ status: string }> {
    return this.request('/health')
  }
  
  /**
   * Get list of available operators
   */
  async getOperators(): Promise<Operator[]> {
    return this.request('/operators')
  }

  /**
   * Convert operator UUIDs to names for sensor endpoint queries
   * Backend sensor endpoints require operator NAME, not UUID
   */
  private async resolveOperatorNames(operatorIds: string[]): Promise<string[]> {
    const operators = await this.getOperators()
    const operatorMap = new Map(operators.map(op => [op.id, op.name]))
    return operatorIds.map(id => operatorMap.get(id) || id).filter(Boolean)
  }
  

  /**
   * Find zones near a location - RECOMMENDED for initial discovery
   * Returns zones + operator_ids to subscribe to
   */
  async getNearbyZones(
    latitude: number,
    longitude: number,
    radius: number = 1000
  ): Promise<NearbyZonesResponse> {
    const params = new URLSearchParams()
    params.append('lat', latitude.toString())
    params.append('lon', longitude.toString())
    params.append('radius', radius.toString())
    return this.request(`/zones?${params.toString()}`)
  }
  
  /**
   * Garages and lots near a location, with how many spaces are free on each floor
   */
  async getNearbyFacilities(latitude: number, longitude: number, radius: number = 1000): Promise<Facility[]> {
    const params = new URLSearchParams()
    params.append('lat', latitude.toString())
    params.append('lon', longitude.toString())
    params.append('radius', Math.round(radius).toString())
    return (await this.request<{ facilities: Facility[] }>(`/facilities?${params.toString()}`)).facilities
  }

  /**
   * Get combined sensor data (static + state) for an operator or by location
   * Falls back if endpoint unavailable
   */
  async getSensors(operatorId?: string, location?: { lat: number; lon: number; radius: number }): Promise<Sensor[]> {
    try {
      const params = new URLSearchParams()
      
      if (operatorId !== undefined) {
        params.append('operator_id', operatorId)
      } else if (location) {
        params.append('lat', location.lat.toString())
        params.append('lon', location.lon.toString())
        params.append('radius', location.radius.toString())
      }
      
      return this.request(`/sensors?${params.toString()}`)
    } catch (e) {
      // Fallback 1: If operator UUID failed, try resolving to name
      if (operatorId && (e as Error).message?.includes('operator')) {
        try {
          const names = await this.resolveOperatorNames([operatorId])
          if (names.length > 0) {
            const params = new URLSearchParams()
            params.append('operator_id', names[0])
            return this.request(`/sensors?${params.toString()}`)
          }
        } catch {}
      }
      
      // Fallback 2: use nearby zones
      if (location) {
        const nearbyZones = await this.getNearbyZones(location.lat, location.lon, location.radius)
        return nearbyZones.zones as unknown as Sensor[]
      }
      
      return []
    }
  }
  
  /**
   * Calculate distance between two points using Haversine formula (in km)
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371 // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lon2 - lon1) * Math.PI / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) *
        Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }
  async getSensorsByLocationWithMetadata(
    latitude: number,
    longitude: number,
    radius: number = 500
  ): Promise<MapSensor[]> {
    // First get nearby zones to discover operators and zones
    const nearbyResponse = await this.getNearbyZones(latitude, longitude, radius)
    const zones = nearbyResponse.zones
    const operatorIds = nearbyResponse.operator_ids
    
    if (operatorIds.length === 0) {
      return []
    }
    
    // Try UUIDs first, getStaticSnapshot/getStateSnapshot will fall back to names if needed
    // Get static snapshots for all operators (contains coordinates, zone assignments)
    const staticSnapshots = await Promise.all(
      operatorIds.map(operatorId => this.getStaticSnapshot(operatorId))
    )
    const allStaticSensors = staticSnapshots.flat()
    
    // Filter static sensors by location (within radius)
    const nearbyStaticSensors = allStaticSensors.filter(sensor => {
      const distance = this.calculateDistance(latitude, longitude, sensor.lat, sensor.lon)
      return distance <= radius / 1000 // radius is in meters, distance in km
    })
    
    // Get state snapshots for all operators (contains real-time occupancy)
    const stateSnapshots = await Promise.all(
      operatorIds.map(operatorId => this.getStateSnapshot(operatorId))
    )
    const allStateSensors = stateSnapshots.flat()
    
    // Create state lookup map
    const stateMap = new Map(allStateSensors.map(state => [state.uuid, state]))
    
    // Get operators data for display names
    const operators = await this.getOperators()
    const operatorMap = new Map(operators.map(op => [op.id, op]))
    
    // Create zone map for joining
    const zoneMap = new Map(zones.map(z => [z.zone_id, z]))
    
    // Join static sensor data with state data and metadata
    return nearbyStaticSensors.map(staticSensor => {
      const stateSensor = stateMap.get(staticSensor.uuid)
      const zone = staticSensor.zone_id ? zoneMap.get(staticSensor.zone_id) : undefined
      const operator = operatorMap.get(staticSensor.operator_id)
      
      return {
        uuid: staticSensor.uuid,
        space_number: staticSensor.space_number,
        zone_id: staticSensor.zone_id,
        operator_id: staticSensor.operator_id,
        lat: staticSensor.lat,
        lon: staticSensor.lon,
        occupancy_status: stateSensor?.occupancy_status ?? null,
        occupancy_timestamp: stateSensor?.occupancy_timestamp ?? null,
        isOccupied: stateSensor?.occupancy_status === true,
        isAvailable: stateSensor?.occupancy_status === false,
        zone_name: zone?.display_name,
        zone_rules: zone?.rules,
        operator_name: operator?.display_name,
        friendly_name: staticSensor.friendly_name,
        parking_space_boundary: staticSensor.parking_space_boundary,
        level_id: staticSensor.level_id ?? null,
      }
    })
  }
  
  /**
   * Get combined sensor data by location
   * Finds all zones in radius, returns their sensors
   */
  async getSensorsByLocation(
    latitude: number,
    longitude: number,
    radius: number = 500
  ): Promise<Sensor[]> {
    return this.getSensors(undefined, { lat: latitude, lon: longitude, radius })
  }
  
  /**
   * Get static sensor snapshot for an operator or by location
   * Falls back if endpoint unavailable
   */
  async getStaticSnapshot(operatorId?: string, location?: { lat: number; lon: number; radius: number }): Promise<StaticSensor[]> {
    try {
      const params = new URLSearchParams()
      
      if (operatorId !== undefined) {
        params.append('operator_id', operatorId)
      } else if (location) {
        params.append('lat', location.lat.toString())
        params.append('lon', location.lon.toString())
        params.append('radius', location.radius.toString())
      }
      
      const sensors = await this.request<StaticSensor[]>(`/sensors/static?${params.toString()}`)
      
      // Parse parking_space_boundary if it's a string
      return sensors.map(sensor => ({
        ...sensor,
        parking_space_boundary: parseParkingSpaceBoundary(sensor.parking_space_boundary)
      }))
    } catch (e) {
      // Fallback 1: If operator UUID failed, try resolving to name
      if (operatorId && (e as Error).message?.includes('operator')) {
        try {
          const names = await this.resolveOperatorNames([operatorId])
          if (names.length > 0) {
            const params = new URLSearchParams()
            params.append('operator_id', names[0])
            const sensors = await this.request<StaticSensor[]>(`/sensors/static?${params.toString()}`)
            
            // Parse parking_space_boundary if it's a string
            return sensors.map(sensor => ({
              ...sensor,
              parking_space_boundary: parseParkingSpaceBoundary(sensor.parking_space_boundary)
            }))
          }
        } catch {}
      }
      
      // Fallback 2: use nearby zones
      if (location) {
        const nearbyZones = await this.getNearbyZones(location.lat, location.lon, location.radius)
        return nearbyZones.zones as unknown as StaticSensor[]
      }
      return []
    }
  }
  
  /**
   * Get current state snapshot for an operator or by location
   * Falls back if endpoint unavailable
   */
  async getStateSnapshot(operatorId?: string, location?: { lat: number; lon: number; radius: number }): Promise<SensorState[]> {
    try {
      const params = new URLSearchParams()
      
      if (operatorId !== undefined) {
        params.append('operator_id', operatorId)
      } else if (location) {
        params.append('lat', location.lat.toString())
        params.append('lon', location.lon.toString())
        params.append('radius', location.radius.toString())
      }
      
      return this.request(`/sensors/state?${params.toString()}`)
    } catch (e) {
      // Fallback 1: If operator UUID failed, try resolving to name
      if (operatorId && (e as Error).message?.includes('operator')) {
        try {
          const names = await this.resolveOperatorNames([operatorId])
          if (names.length > 0) {
            const params = new URLSearchParams()
            params.append('operator_id', names[0])
            return this.request(`/sensors/state?${params.toString()}`)
          }
        } catch {}
      }
      
      // Fallback 2: use nearby zones (state snapshot not critical for display)
      if (location) {
        const nearbyZones = await this.getNearbyZones(location.lat, location.lon, location.radius)
        return nearbyZones.zones as unknown as SensorState[]
      }
      return []
    }
  }
  
  /**
   * Get sensors for a specific zone
   */
  async getZoneSnapshot(zoneId: string): Promise<StaticSensor[]> {
    const params = new URLSearchParams()
    params.append('zone_id', zoneId)
    return this.request(`/sensors/static?${params.toString()}`)
  }
  
  /**
   * Build map data for multiple operators using nearby zones
   * Discovers operators from location rather than explicit list
   */
  async buildMapDataFromLocation(latitude: number, longitude: number, radius: number = 1000): Promise<MapSensor[]> {
    try {
      const nearbyResponse = await this.getNearbyZones(latitude, longitude, radius)
      const zones = nearbyResponse.zones || []
      
      return zones.map(zone => ({
        uuid: zone.zone_id,
        space_number: null,
        zone_id: zone.zone_id,
        operator_id: zone.operator_id,
        lat: zone.center_coordinates?.lat || 0,
        lon: zone.center_coordinates?.lon || 0,
        occupancy_status: null,
        occupancy_timestamp: null,
        isOccupied: false,
        isAvailable: false,
        zone_name: zone.display_name,
        zone_rules: zone.rules,
        operator_name: undefined, // Would need separate operator lookup
      }))
    } catch (e) {
      console.error('Failed to build map data:', e)
      return []
    }
  }
}

// Export singleton instance
const apiClient = new ApiClient()

// React Query hooks
import { useQuery } from '@tanstack/react-query'

export function useOperators() {
  return useQuery({
    queryKey: ['operators'],
    queryFn: () => apiClient.getOperators(),
    staleTime: 60 * 60 * 1000, // 1 hour (operator metadata doesn't change often)
  })
}

/** Garages near the map's centre and their floors' free spaces (decision 49). */
export function useFacilitiesByLocation(latitude: number, longitude: number, radius: number = 5000, enabled = true) {
  return useQuery({
    queryKey: ['facilities', latitude, longitude, radius],
    queryFn: () => apiClient.getNearbyFacilities(latitude, longitude, radius),
    enabled: enabled && !!latitude && !!longitude,
    staleTime: STALE_TIME.SENSORS,
    refetchInterval: STALE_TIME.SENSORS,
  })
}

// Cache keys for localStorage
const CACHE_KEY_SENSORS = 'parkeze-sensors-cache'
const CACHE_KEY_ZONES = 'parkeze-zones-cache'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes (matches worker state TTL)

// React Query staleTime values (should match or be less than worker cache TTLs)
const STALE_TIME = {
  STATIC: 60 * 60 * 1000,    // 1 hour - operators, zones, static
  STATE: 5 * 60 * 1000,      // 5 minutes - state (occupancy)
  SENSORS: 60 * 1000,        // 1 minute - combined sensors endpoint
}

interface CachedData<T> {
  data: T
  timestamp: number
  lat: number
  lon: number
  radius: number
}

function getCachedData<T>(key: string, lat: number, lon: number, _radius: number): T | null {
  try {
    const cached = localStorage.getItem(key)
    if (!cached) return null
    
    const parsed: CachedData<T> = JSON.parse(cached)
    const now = Date.now()
    
    // Check if cache is still valid and for same location
    const isSameLocation = Math.abs(parsed.lat - lat) < 0.01 && Math.abs(parsed.lon - lon) < 0.01
    const isNotExpired = (now - parsed.timestamp) < CACHE_DURATION
    
    if (isSameLocation && isNotExpired) {
      return parsed.data
    }
  } catch {
    // Invalid cache, ignore
  }
  return null
}

function setCachedData<T>(key: string, data: T, lat: number, lon: number, radius: number): void {
  try {
    const cached: CachedData<T> = {
      data,
      timestamp: Date.now(),
      lat,
      lon,
      radius
    }
    localStorage.setItem(key, JSON.stringify(cached))
  } catch {
    // Storage full or unavailable, ignore
  }
}

/**
 * Hook to get sensors and zones by location together (avoids duplicate API calls)
 * Returns both sensor data and zone metadata in one request
 * Uses localStorage cache for instant load on page refresh (theme changes)
 */
export function useSensorsAndZonesByLocation(latitude: number, longitude: number, radius: number = 5000, enabled = true) {
  return useQuery({
    queryKey: ['sensorsAndZones', latitude, longitude, radius],
    queryFn: async () => {
      // Check localStorage cache first for instant theme-switch reload
      const cachedSensors = getCachedData<MapSensor[]>(CACHE_KEY_SENSORS, latitude, longitude, radius)
      const cachedZones = getCachedData<Zone[]>(CACHE_KEY_ZONES, latitude, longitude, radius)
      
      if (cachedSensors && cachedZones) {
        // Return cached data immediately, but also refresh in background
        setTimeout(async () => {
          try {
            const zonesResponse = await apiClient.getNearbyZones(latitude, longitude, radius)
            const zones = zonesResponse.zones
            const sensors = await apiClient.getSensorsByLocationWithMetadata(latitude, longitude, radius)
            setCachedData(CACHE_KEY_SENSORS, sensors, latitude, longitude, radius)
            setCachedData(CACHE_KEY_ZONES, zones, latitude, longitude, radius)
          } catch {
            // Background refresh failed, use cache
          }
        }, 100)
        
        return { sensors: cachedSensors, zones: cachedZones }
      }
      
      // No cache, fetch fresh data
      const zonesResponse = await apiClient.getNearbyZones(latitude, longitude, radius)
      const zones = zonesResponse.zones
      const sensors = await apiClient.getSensorsByLocationWithMetadata(latitude, longitude, radius)
      
      // Cache for next reload
      setCachedData(CACHE_KEY_SENSORS, sensors, latitude, longitude, radius)
      setCachedData(CACHE_KEY_ZONES, zones, latitude, longitude, radius)
      
      return { sensors, zones }
    },
    enabled: enabled && !!latitude && !!longitude,
    staleTime: STALE_TIME.STATE, // 5 minutes - matches worker cache
  })
}
